/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.domain.calc

import kpt.core.model.emi.EmiResult
import kotlin.math.pow

/**
 * Standalone EMI math used by the B2 Wizard, B3 Amortization, B5 Affordability,
 * and B6 Comparison calculators.
 *
 * Mirrors [kpt.core.domain.emi.calculateEmi] in formula but additionally
 * exposes [amortizationSchedule] for installment-by-installment breakdowns.
 * Pure functions — safe to call inline from any ViewModel.
 *
 * **Precision**: monetary values are returned as [Double]. The UI is expected to
 * present them at 2 dp. For full-`BigDecimal` precision, replace the math layer
 * with a multiplatform `BigDecimal` port — overkill for a utility template.
 */

/**
 * Compute the standard reducing-balance EMI.
 *
 * Formula: `EMI = P · r · (1+r)^n / ((1+r)^n − 1)` where `r = annualRate/12/100`.
 *
 * Edge cases:
 * - `principal <= 0` or `tenureMonths <= 0` → returns a zero [EmiResult]
 *   (caller doesn't have to guard before rendering).
 * - `annualRatePercent == 0` → falls back to `principal / tenureMonths`
 *   (no interest accrues).
 */
fun computeEmi(principal: Double, annualRatePercent: Double, tenureMonths: Int): EmiResult {
    if (principal <= 0.0 || tenureMonths <= 0) {
        return EmiResult(emi = 0.0, totalPayment = 0.0, totalInterest = 0.0)
    }
    val monthlyRate = annualRatePercent / 12.0 / 100.0
    val n = tenureMonths.toDouble()
    val emi = if (monthlyRate == 0.0) {
        principal / n
    } else {
        val factor = (1.0 + monthlyRate).pow(n)
        principal * monthlyRate * factor / (factor - 1.0)
    }
    val totalPayment = emi * tenureMonths
    return EmiResult(
        emi = emi,
        totalPayment = totalPayment,
        totalInterest = totalPayment - principal,
    )
}

/**
 * Build the month-by-month amortization schedule for a loan.
 *
 * The list is generated by the standard reducing-balance method:
 *
 * 1. Compute the level EMI via [computeEmi].
 * 2. For each installment, split the EMI into:
 *    - `interestPaid = balance · monthlyRate`
 *    - `principalPaid = emi − interestPaid`
 * 3. Decrement the outstanding balance.
 *
 * The last row's balance may be a few cents away from zero (floating-point
 * accumulation) — the UI presents this rounded to 2 dp.
 *
 * Returns an empty list for non-positive principal or tenure so a caller can
 * pipe straight into a `LazyColumn` without guarding.
 */
fun amortizationSchedule(principal: Double, annualRatePercent: Double, tenureMonths: Int): List<AmortizationRow> {
    if (principal <= 0.0 || tenureMonths <= 0) return emptyList()
    val monthlyRate = annualRatePercent / 12.0 / 100.0
    val emi = computeEmi(principal, annualRatePercent, tenureMonths).emi
    val rows = ArrayList<AmortizationRow>(tenureMonths)
    var balance = principal
    for (installment in 1..tenureMonths) {
        val interestPaid = balance * monthlyRate
        val principalPaid = emi - interestPaid
        balance -= principalPaid
        rows += AmortizationRow(
            installmentNumber = installment,
            principalPaid = principalPaid,
            interestPaid = interestPaid,
            balanceRemaining = balance,
        )
    }
    return rows
}

/**
 * A single line in an amortization schedule.
 *
 * @property installmentNumber 1-based month index.
 * @property principalPaid Portion of [installmentNumber]'s EMI that reduces principal.
 * @property interestPaid Portion of [installmentNumber]'s EMI that pays interest.
 * @property balanceRemaining Outstanding balance after this installment.
 */
data class AmortizationRow(
    val installmentNumber: Int,
    val principalPaid: Double,
    val interestPaid: Double,
    val balanceRemaining: Double,
)
